import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';
import { formatProgressCell, shooterPercentOfBest, isConventionMember, isConventionOrganizer } from '../../lib/conventionResults';
import { appConfirm } from '../../lib/dialogs';
import BarChartVertical from '../gui/barChartVertical';
import BarChartHorizontal from '../gui/barChartHorizontal';
import { isIpscConvention } from '../../lib/ipscParticipant';
import { stageDefFromStand, scoreSheet } from '../../lib/ipscScoring';
import { formatHf } from '../../lib/ipscResultsView';

@observer
class ShooterDetail extends Component {
    openUploadImage = (detail) => (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('uploadUserImage', {
            userId: detail.participantId,
            name: `${detail.firstname} ${detail.lastname}`,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    openEditParticipant = (detail) => (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('editConventionParticipant', {
            id: detail.conventionId,
            md5: detail.participantMd5,
            stage: detail.stageMd5,
            idx: detail.lagIdx - 1,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    // IPSC (dynamisk): the shooter's scoresheets, one row per stage, with approval state.
    deleteIpscSheet = (id) => async (e) => {
        e?.preventDefault?.();
        const { conventionIpscScoreStore } = this.props.stores;
        if (!(await appConfirm('Slette dette poengskjemaet?'))) return;
        await conventionIpscScoreStore.removeSheet(id);
    }

    renderIpsc(convention, participantId) {
        const { conventionIpscScoreStore, userStore } = this.props.stores;
        const { user = {}, isAdmin } = userStore || {};
        const isOfficial = isAdmin || isConventionMember(convention, user) || isConventionOrganizer(convention, user) || (!!user.id && convention.user === user.id);
        const rows = (conventionIpscScoreStore.conventionIpscScores || [])
            .filter(r => r.participant === participantId)
            .slice()
            .sort((a, b) => a.stand - b.stand);
        const name = rows.length ? (rows[0].scoredFor || '') : '';
        return (
            <div class='p-3'>
                <h5 class='mb-2'>{name || <Text id='ipsc.shooter'>Shooter</Text>} <small class='text-body-secondary'>#{participantId}</small></h5>
                {rows.length === 0 && <div class='text-body-secondary'><Text id='ipsc.no-results-yet'>No scoresheets yet.</Text></div>}
                {rows.map((r) => {
                    const def = stageDefFromStand((convention.stageStands || []).find(s => Number(s.stand) === r.stand) || {});
                    const calc = scoreSheet(r, def, r.powerFactor);
                    return (
                        <div key={r.id} class={`rounded-3 border p-2 mb-2 ${r.dq ? 'border-danger' : ''}`}>
                            <div class='d-flex flex-row justify-content-between'>
                                <span class='fw-bold'><Text id='ipsc.stage'>Stage</Text> {r.stand} · {r.division} · {r.powerFactor === 'major' ? 'Major' : 'Minor'}</span>
                                <span class='fw-bold'>HF {formatHf(calc.hitFactor)}</span>
                            </div>
                            <div class='small'>
                                {r.dq ? <span class='text-danger'><Text id='ipsc.dq'>DQ</Text> {r.dqRule}</span>
                                    : r.dnf ? <span class='text-warning'><Text id='ipsc.dnf'>DNF</Text></span>
                                        : <>{r.hitsA}A {r.hitsC}C {r.hitsD}D {r.misses}M · steel {r.steelHits} · NS {r.noShoots} · proc {r.procedurals} · {Number(r.time || 0).toFixed(2)} s · {calc.stageScore} p</>}
                            </div>
                            <div class='small text-body-secondary'>
                                {r.approvedAt
                                    ? <><i class='fa-regular fa-check text-success me-1' /><Text id='ipsc.status-approved'>Approved</Text>{r.approvedByName ? ` · ${r.approvedByName}` : ''}{r.editedAfterApproval ? <span class='ms-1 text-warning'>· <Text id='ipsc.status-edited'>Edited after approval</Text></span> : null}</>
                                    : <Text id='ipsc.status-synced'>Scored</Text>}
                                {' · v'}{r.version}{r.scoredByName ? ` · RO ${r.scoredByName}` : ''}
                                {isOfficial && (
                                    <button type='button' class='btn btn-sm btn-link text-danger p-0 ms-2' onClick={this.deleteIpscSheet(r.id)}>
                                        <i class='fa-regular fa-trash me-1' /><Text id='ipsc.delete-sheet'>Delete sheet</Text>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const { participantId, stageMd5 } = this.props;
        const { conventionStore, conventionResultStore } = this.props.stores;
        const { convention, participants, classMap, disciplineMap } = conventionStore;
        const { conventionResults } = conventionResultStore;

        if (isIpscConvention(convention)) {
            return this.renderIpsc(convention, participantId);
        }

        const detail = conventionResultStore.getShooterDetail(
            convention,
            conventionResults || [],
            participants || [],
            { participantId, stageMd5 },
            { classMap, disciplineMap },
        );

        if (!detail) {
            return (
                <div class='p-3'>
                    <Text id='convention.no-results-yet-short'>No results.</Text>
                </div>
            );
        }

        const isTime = detail.stageType === 'tidsoving' || detail.stageType === 'tidskonk';
        const isT96 = detail.stageType === 't-96';

        // "% av beste": this start against the best sum in the same weapon class today.
        const resultList = conventionResultStore.getResultsListForAllClasses(
            convention, conventionResults || [], participants || [], classMap, disciplineMap,
        );
        const percentOfBestToday = shooterPercentOfBest(resultList, participantId, stageMd5);

        const barData = detail.perStand.map(p => ({ label: String(p.stand), value: p.value }));
        const horizontalData = [
            { label: 'Skudd', value: detail.aggregates.scoreHits, color: '#212529' },
            { label: 'Skive', value: detail.aggregates.scoreTargets, color: '#0d6efd' },
            { label: 'Sone', value: detail.aggregates.scoreInner, color: '#d63384' },
        ];

        return (
            <div class='p-3'>
                {this.renderHeader(detail, percentOfBestToday)}
                {detail.perStand.length === 0 ? (
                    <div class='alert alert-info my-3'>
                        <Text id='convention.no-results-yet-short'>No results.</Text>
                    </div>
                ) : (<>
                    {this.renderStandTable(detail, isT96, isTime)}

                    <div class='mt-4 mb-2 fw-semibold text-center'>
                        <Text id='convention.series'>Series</Text>
                    </div>
                    <BarChartVertical data={barData} height={220} />

                    {!isTime && (<>
                        <div class='mt-4 mb-2 fw-semibold text-center'>
                            <Text id='convention.totalt'>Total</Text>
                        </div>
                        <BarChartHorizontal data={horizontalData} />
                    </>)}

                    {isTime && this.renderTimeAggregates(detail)}
                </>)}
            </div>
        );
    }

    renderHeader(detail, percentOfBestToday) {
        const { userStore, conventionStore } = this.props.stores;
        const { user = {}, isAdmin } = userStore || {};
        const { participants = [] } = conventionStore || {};
        const isYou = user.id && detail.participantId === user.id;
        const canEdit = (isAdmin || isYou) && detail.participantMd5;
        const canUploadImage = isAdmin || isYou;

        // Avatar from the included user records (image fields are projected on
        // single-convention loads); camera badge opens the dead-simple uploader.
        const userRecord = participants.find(u => u.id === detail.participantId);
        const image = userRecord ? localUtil.getImage({ user: userRecord }) : null;
        const avatar = (
            <div style='position: relative; flex: 0 0 auto;'>
                {image ? (
                    <img
                        src={image}
                        alt={`${detail.firstname} ${detail.lastname}`}
                        class='rounded-circle bg-body-secondary'
                        style='width: 64px; height: 64px; object-fit: cover;'
                    />
                ) : (
                    <div
                        class='rounded-circle bg-body-secondary d-flex align-items-center justify-content-center'
                        style='width: 64px; height: 64px;'
                    >
                        <i class='fa-solid fa-user text-body-tertiary' style='font-size: 26px;' />
                    </div>
                )}
                {canUploadImage && (
                    <button
                        type='button'
                        class='btn btn-primary rounded-circle p-0 d-flex align-items-center justify-content-center position-absolute shadow-sm'
                        style='width: 28px; height: 28px; bottom: -4px; right: -4px; font-size: 13px;'
                        onClick={this.openUploadImage(detail)}
                        title='Last opp profilbilde'
                    >
                        <i class='fa-solid fa-camera' />
                    </button>
                )}
            </div>
        );

        return (
            <div class='mb-3'>
                <div class='d-flex flex-row align-items-start gap-3'>
                    {avatar}
                    <div style='min-width: 0; flex: 1 1 auto;'>
                    <h4 class='mb-0' style='padding-right: 60px;'>{detail.firstname} {detail.lastname}</h4>
                    <div class='text-body-secondary'>{detail.sportsClub}</div>
                    <div class='small mt-1'>
                        {detail.stageTypeDisplay} {detail.weaponDisplay}
                        {detail.skillClass && (<>
                            {' :: '}<Text id='convention.klasse'>Class</Text> {detail.skillClass}
                        </>)}
                    </div>
                    {detail.lagIdx > 0 && (
                        <div class='small text-body-secondary'>
                            <Text id='convention.lag'>Lag</Text> {detail.lagIdx}
                        </div>
                    )}
                    <div class='d-flex gap-4 mt-2 align-items-center'>
                        <div>
                            <span class='small text-body-secondary me-1'>
                                <Text id='convention.results-sum'>Sum</Text>
                            </span>
                            <span class='fs-5 fw-bold'>{detail.totalScore}</span>
                        </div>
                        <div>
                            <span class='small text-body-secondary me-1'>iX</span>
                            <span class='fs-5 fw-bold'>{detail.totalIx}</span>
                        </div>
                        {percentOfBestToday && (
                            <div>
                                <span class='fs-5 fw-bold'>{percentOfBestToday.percent} %</span>
                                <span class='small text-body-secondary ms-1'>
                                    <Text id='convention.results-of-best-in' fields={{ weapon: detail.weaponDisplay }}>
                                        of the best in {detail.weaponDisplay}
                                    </Text>
                                </span>
                            </div>
                        )}
                        {canEdit && (
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-primary rounded-pill ms-auto'
                                onClick={this.openEditParticipant(detail)}
                            >
                                <i class='fa-regular fa-pen-to-square me-1' />
                                <Text id='convention.edit-signup'>Edit signup</Text>
                            </button>
                        )}
                    </div>
                    </div>
                </div>
            </div>
        );
    }

    renderStandTable(detail, isT96, isTime) {
        if (isTime) {
            return (
                <div class='table-responsive'>
                    <table class='table table-sm align-middle'>
                        <thead>
                            <tr>
                                <th><Text id='convention.stand'>Stand</Text></th>
                                <th class='text-end'><Text id='convention.results-time'>Time</Text></th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.perStand.map(p => (
                                <tr key={p.stand}>
                                    <td>{p.stand}</td>
                                    <td class='text-end font-monospace'>
                                        {parseFloat(p.value).toFixed(2)}s
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        const subTotalRows = isT96 ? new Set([6, 12, 16]) : new Set();
        let runningSum = 0;
        let lastBoundary = 0;

        return (
            <div class='table-responsive'>
                <table class='table table-sm align-middle'>
                    <thead>
                        <tr>
                            <th>
                                {isT96
                                    ? <Text id='convention.series'>Series</Text>
                                    : <Text id='convention.stand'>Stand</Text>}
                            </th>
                            <th><Text id='convention.skudd-skive-sone'>Hits / Targets / Inner (Penalty)</Text></th>
                            <th class='text-end'>iX</th>
                            <th class='text-end'><Text id='convention.poeng'>Points</Text></th>
                            {isT96 && <th class='text-end'><Text id='convention.delsum'>Sub-total</Text></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {detail.perStand.map(p => {
                            const r = p.result;
                            runningSum += p.value;
                            const isBoundary = subTotalRows.has(p.stand);
                            const subTotal = isBoundary ? runningSum - lastBoundary : null;
                            if (isBoundary) lastBoundary = runningSum;
                            return (
                                <tr key={p.stand}>
                                    <td>{p.stand}</td>
                                    <td class='font-monospace'>
                                        {(r.scoreHits || 0)} / {(r.scoreTargets || 0)} / {(r.scoreInner || 0)} ({r.scorePenalty || 0})
                                    </td>
                                    <td class='text-end'>{r.scoreInner || 0}</td>
                                    <td class='text-end fw-bold'>{p.value}</td>
                                    {isT96 && (
                                        <td class='text-end fw-bold'>
                                            {subTotal != null ? subTotal : ''}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        <tr class='border-top border-2'>
                            <td colSpan={isT96 ? 2 : 2}>
                                <strong><Text id='convention.totalt'>Total</Text></strong>
                            </td>
                            <td class='text-end fw-bold'>{detail.totalIx}</td>
                            <td class='text-end fw-bold'>{detail.totalScore}</td>
                            {isT96 && <td />}
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    renderTimeAggregates(detail) {
        const times = detail.perStand.map(p => p.value).filter(v => v > 0);
        if (!times.length) return null;
        const best = Math.min(...times);
        const sum = times.reduce((a, b) => a + b, 0);
        const avg = sum / times.length;
        return (
            <div class='d-flex justify-content-around mt-3'>
                <div class='text-center'>
                    <div class='small text-body-secondary'><Text id='convention.tid-best'>Best</Text></div>
                    <div class='fs-5 fw-bold'>{best.toFixed(2)}s</div>
                </div>
                <div class='text-center'>
                    <div class='small text-body-secondary'><Text id='convention.tid-avg'>Avg</Text></div>
                    <div class='fs-5 fw-bold'>{avg.toFixed(2)}s</div>
                </div>
                <div class='text-center'>
                    <div class='small text-body-secondary'><Text id='convention.tid-sum'>Total</Text></div>
                    <div class='fs-5 fw-bold'>{sum.toFixed(2)}s</div>
                </div>
            </div>
        );
    }
}

export default ShooterDetail;
